import React from 'react';
import type { BulkUploadResults as BulkUploadResultsType } from '../../types';
import Modal from '../ui/Modal';

interface BulkUploadResultsProps {
  results: BulkUploadResultsType;
  onClose: () => void;
}

const BulkUploadResults: React.FC<BulkUploadResultsProps> = ({ results, onClose }) => {
  const { success, errors, skipped } = results;

  return (
    <Modal title="Bulk Upload Results" onClose={onClose} maxWidth="4xl">
      <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{success.length}</div>
            <div className="text-sm text-green-700">Successfully Added</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{skipped.length}</div>
            <div className="text-sm text-yellow-700">Skipped (Already Exists)</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{errors.length}</div>
            <div className="text-sm text-red-700">Errors</div>
          </div>
        </div>

        {success.length > 0 && (
          <div>
            <h4 className="text-md font-semibold text-green-700 mb-3">Successfully Added Users</h4>
            <div className="bg-green-50 rounded-lg p-4 max-h-40 overflow-y-auto">
              {success.map((item, index) => (
                <div key={index} className="text-sm text-green-800 mb-2">
                  <strong>Row {item.row}:</strong> {item.data.name} ({item.data.email}) - {item.data.status}
                </div>
              ))}
            </div>
          </div>
        )}

        {skipped.length > 0 && (
          <div>
            <h4 className="text-md font-semibold text-yellow-700 mb-3">Skipped Users (Already Exist)</h4>
            <div className="bg-yellow-50 rounded-lg p-4 max-h-40 overflow-y-auto">
              {skipped.map((item, index) => (
                <div key={index} className="text-sm text-yellow-800 mb-2">
                  <strong>Row {item.row}:</strong> {(item.data as any).name} ({(item.data as any).email}) - {item.reason}
                </div>
              ))}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div>
            <h4 className="text-md font-semibold text-red-700 mb-3">Errors</h4>
            <div className="bg-red-50 rounded-lg p-4 max-h-40 overflow-y-auto">
              {errors.map((item, index) => (
                <div key={index} className="text-sm text-red-800 mb-2">
                  <strong>Row {item.row}:</strong> {item.reason}
                  {(item.data as any).name && ` - ${(item.data as any).name} (${(item.data as any).email})`}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-md font-semibold text-blue-700 mb-2">File Format Requirements</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)</p>
            <p><strong>Required columns:</strong> name, email, status</p>
            <p><strong>Status values:</strong> active, inactive (case-insensitive)</p>
            <p><strong>Email format:</strong> Must be valid email address</p>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUploadResults;
